/**
 * Total-Score: gewichtete Summe der erfassten Dimensions-Scores.
 *
 * Gewichtung gemäß Konzept §5.2:
 *   Identity   15%
 *   Activity   20%
 *   Style      15%
 *   Resources  30%
 *   Health     10%
 *   Private    10%
 *   ---------- 100%
 *
 * Untouched Dimensionen werden NICHT als „0" mitgewichtet (das hätte zur
 * Folge, dass eine perfekt erfasste Resources-Dimension nur ~30/100 ergäbe,
 * obwohl alle erfassten Daten Top-Wert haben). Stattdessen wird der Total-
 * Score über die Gewichtssumme der tatsächlich gewerteten Dimensionen
 * normalisiert:
 *
 *   totalScore = Σ(weight_i · score_i)  /  Σ(weight_i)   für i ∈ filled
 *
 * Bei leeren `filled` → 0 (UI muss state='unscored' separat anzeigen).
 *
 * Ergebnis wird gerundet und auf [0,100] gecappt.
 */
import type { ScoreValue } from '../schema/brands.js';
import type { DimensionKey } from '../schema/dimensionData.js';
import { clampScore } from './internal/clamp.js';

export const DIMENSION_WEIGHTS: Readonly<Record<DimensionKey, number>> = {
  identity: 0.15,
  activity: 0.2,
  style: 0.15,
  resources: 0.3,
  health: 0.1,
  private: 0.1,
};

/**
 * Partial-Map: nur erfasste Dimensionen sind enthalten. Fehlt ein Schlüssel
 * → Dimension wurde nicht erfasst und fließt nicht in den Total-Score ein.
 */
export type DimensionScoreMap = Readonly<Partial<Record<DimensionKey, ScoreValue>>>;

export function computeTotalScore(scores: DimensionScoreMap): ScoreValue {
  let weightedSum = 0;
  let weightSum = 0;
  for (const dim of Object.keys(DIMENSION_WEIGHTS) as DimensionKey[]) {
    const s = scores[dim];
    if (s === undefined) continue;
    const w = DIMENSION_WEIGHTS[dim];
    weightedSum += (s as number) * w;
    weightSum += w;
  }
  if (weightSum === 0) return clampScore(0);
  return clampScore(weightedSum / weightSum);
}
